import type {
  ConsentKitConfig,
  ConsentRecord,
  Decision,
  Dictionary,
  LocaleCode,
  ResolvedRegion,
  VendorDefinition,
} from "../types";
import { prefersDark, resolveTokens, tokensToCss } from "../theme/tokens";
import { createBanner } from "./banner";
import { createPreferences } from "./preferences";
import { createRevisitButton } from "./revisit";
import { createRoot, documentDirection, injectStyles } from "./dom";
import { onEscape, trapFocus, type FocusTrap } from "./a11y";
import { STYLES } from "./styles";

export interface UiDeps {
  config: ConsentKitConfig;
  t: Dictionary;
  locale: LocaleCode;
  region: ResolvedRegion;
  vendors: VendorDefinition[];
  onDecision(decision: Decision, method: ConsentRecord["method"]): void;
  currentDecision(): Decision;
}

/** Owns the shadow root and the three surfaces that live in it. */
export class ConsentUi {
  private readonly host: HTMLElement;
  private readonly root: ShadowRoot | HTMLElement;
  private readonly container: HTMLElement;

  private banner: HTMLElement | null = null;
  private prefs: { overlay: HTMLElement; panel: HTMLElement } | null = null;
  private revisit: HTMLElement | null = null;
  private trap: FocusTrap | null = null;
  private releaseEscape: (() => void) | null = null;

  constructor(private readonly deps: UiDeps) {
    const { host, root } = createRoot(deps.config.shadowDom);
    this.host = host;
    this.root = root;

    injectStyles(root, STYLES);
    this.container = document.createElement("div");
    this.container.className = "ck-wrap";
    this.container.setAttribute("dir", documentDirection());
    root.appendChild(this.container);

    this.applyTokens();
    this.watchColorScheme();
  }

  /** Serialized tokens, shared with the light-DOM embed placeholders. */
  get tokensCss(): string {
    return tokensToCss(
      resolveTokens(this.deps.config.theme, prefersDark(this.deps.config.theme.scheme), this.deps.config.debug),
    );
  }

  showBanner(): void {
    if (this.banner) return;
    this.hideRevisit();

    const { region } = this.deps;
    const hasThirdCountryTransfer = this.deps.vendors.some((vendor) => vendor.usTransfer);
    this.banner = createBanner(
      {
        config: this.deps.config,
        t: this.deps.t,
        regime: region.regime,
        gpc: region.gpc,
        hasThirdCountryTransfer,
      },
      {
        // Under the opt-out regime the affirmative button *acknowledges* what is already
        // running; it must not re-grant a category an honoured GPC signal just took away,
        // which is what a blanket accept-all would do.
        onAffirm: () =>
          region.regime === "opt-out"
            ? this.deps.onDecision(this.deps.currentDecision(), "accept-all")
            : this.deps.onDecision(this.allGranted(), "accept-all"),
        onDecline: () =>
          region.regime === "opt-out"
            ? this.deps.onDecision(this.optedOut(), "opt-out")
            : this.deps.onDecision(this.allDenied(), "reject-all"),
        onCustomize: () => this.showPreferences(),
      },
    );
    this.container.appendChild(this.banner);
    // Deliberately no focus trap on the first layer: it is not modal, and trapping a
    // visitor who has not decided yet would itself be a dark pattern.
  }

  hideBanner(): void {
    this.banner?.remove();
    this.banner = null;
  }

  showPreferences(): void {
    if (this.prefs) return;

    const view = createPreferences(
      {
        config: this.deps.config,
        t: this.deps.t,
        locale: this.deps.locale,
        regime: this.deps.region.regime,
        vendors: this.deps.vendors,
        current: this.deps.currentDecision(),
      },
      {
        onSave: (decision) => this.deps.onDecision(decision, "custom"),
        onAcceptAll: () => this.deps.onDecision(this.allGranted(), "accept-all"),
        onRejectAll: () => this.deps.onDecision(this.allDenied(), "reject-all"),
        onClose: () => this.closePreferences(),
      },
    );

    this.prefs = view;
    this.container.append(view.overlay, view.panel);
    // Closing this layer returns to the banner without storing anything — an abandoned
    // dialog must never be read as consent.
    view.overlay.addEventListener("click", () => this.closePreferences());
    this.releaseEscape = onEscape(view.panel, () => this.closePreferences());
    this.trap = trapFocus(view.panel);
  }

  closePreferences(): void {
    this.releaseEscape?.();
    this.trap?.release();
    this.releaseEscape = null;
    this.trap = null;
    this.prefs?.overlay.remove();
    this.prefs?.panel.remove();
    this.prefs = null;
    if (!this.banner && !this.revisit) this.showRevisit();
  }

  showRevisit(): void {
    if (!this.deps.config.revisitButton || this.revisit) return;
    this.revisit = createRevisitButton(this.deps.t, this.deps.region.regime, () =>
      this.showPreferences(),
    );
    this.container.appendChild(this.revisit);
  }

  hideRevisit(): void {
    this.revisit?.remove();
    this.revisit = null;
  }

  destroy(): void {
    this.host.remove();
  }

  private allGranted(): Decision {
    const decision = this.allDenied();
    for (const category of this.deps.config.categories.enabled) decision[category] = true;
    return decision;
  }

  private allDenied(): Decision {
    return { necessary: true, functional: false, analytics: false, marketing: false };
  }

  /** "Do Not Sell or Share": only the sale/share categories go off, the rest stays put. */
  private optedOut(): Decision {
    const decision = { ...this.deps.currentDecision() };
    for (const category of this.deps.config.optOutCategories) decision[category] = false;
    return decision;
  }

  private applyTokens(): void {
    const target = this.root instanceof ShadowRoot ? this.container : this.host;
    target.setAttribute("style", this.tokensCss);
  }

  /** With scheme "auto" the banner follows the OS the same way the host page does. */
  private watchColorScheme(): void {
    if (this.deps.config.theme.scheme !== "auto" || typeof matchMedia !== "function") return;
    const query = matchMedia("(prefers-color-scheme: dark)");
    const listener = () => this.applyTokens();
    if (typeof query.addEventListener === "function") query.addEventListener("change", listener);
  }
}
